import React, { useState, useEffect, useRef } from "react";
// Używamy poprawnej ścieżki do DropZone z obecnego kodu
import DropZone from "../DropZone";
// Importujemy funkcje transformacji z kopii zapasowej
import { handleRotate, handleFlipX, handleFlipY } from "./Transforms";
// Importujemy funkcję do pobierania wymiarów SVG z kopii zapasowej
import getSvgDimensions from "../../utils/getSvgDimensions";
// Importujemy mmToPx z obecnego kodu, dodajemy pxToMm z kopii zapasowej
import { mmToPx } from "./utils/rulerUtils";
// Nowy komponent panelu Blissaro
import BlissaroQRPanel from './BlissaroQRPanel';
// Import komponentu Popup
import Popup from '../common/Popup';

// Funkcja pxToMm z kopii zapasowej
function pxToMm(px) {
  // Dodano zabezpieczenie przed NaN/Infinity
  if (typeof px !== 'number' || !isFinite(px)) return 0;
  return (px * 25.4) / 72;
}

// Komponent z logiką z kopii zapasowej
export default function EditorSidePanel({
  backendUrl,
  panelMode,
  setPanelMode,
  selectedShapeId,
  selectedObject,
  onAddNewText,
  onImageDropped,
  onChangeText,
  onLayerUp,
  onLayerDown,
  onLayerTop,
  onLayerBottom,
  availableFonts = [], // Domyślna pusta tablica
  onChangeImage,
  onChangeSvg,
  publicMode,
  projectId, // Nowy prop dla integracji Blissaro
  orderEmail, // Email klienta z zamówienia
  onChangeColor,
  onAddGuide,
  onRemoveGuide,
  onChangeGuide, // Upewnijmy się, że ten prop jest przekazywany
  currentPageData,
  // Usunięto personalizationText i setPersonalizationText, bo nie ma ich w obecnym kodzie
  // onRebusDragStart, // Usunięte, bo nie ma ich w obecnym kodzie
  // onRebusDragEnd, // Usunięte, bo nie ma ich w obecnym kodzie
  onRebusFakeDndStart,
  tooltipsEnabled, // Dodano prop z obecnego kodu
  foldLineConfig, // Nowy prop
  onUpdateFoldLineConfig, // Nowy prop - funkcja do aktualizacji foldLineConfig
  onManualSave, // Nowy prop - funkcja zapisywania projektu z górnej belki
  publicKey, // Nowy prop - klucz publiczny dla Blissaro
  onEmailUpdated // Nowy prop - callback do aktualizacji emaila
}) {
  // Stan dla pozycji i wymiarów w mm (z kopii zapasowej)
  const [xMm, setXmm] = useState("0.00");
  const [yMm, setYmm] = useState("0.00");
  const [wMm, setWmm] = useState("0.00");
  const [hMm, setHmm] = useState("0.00");
  const [heightMm, setHeightMm] = useState("0.00");
  const rebusSelectionRef = useRef(null);

  // Hook integracji z Blissaro przeniesiony do BlissaroQRPanel

  // Efekt aktualizujący wartości mm (z kopii zapasowej)
  useEffect(() => {
    if (selectedObject) {
      // Używamy wartości domyślnych, jeśli brakuje pól
      const { x = 0, y = 0, width = 0, height = 0 } = selectedObject;
      setXmm(pxToMm(x).toFixed(2));
      setYmm(pxToMm(y).toFixed(2));
      // Dla tekstu szerokość/wysokość może być nieustawiona lub auto, obsłużmy to
      setWmm(pxToMm(width || 0).toFixed(2));
      setHmm(pxToMm(height || 0).toFixed(2));
    } else {
      setXmm("0.00");
      setYmm("0.00");
      setWmm("0.00");
      setHmm("0.00");
    }
  }, [selectedObject]);

  // Handler potwierdzenia zmiany pozycji/rozmiaru (z kopii zapasowej)
  const handlePositionSizeConfirm = (field, mmValue) => {
    if (!selectedObject) return;
    // Używamy parseFloat zamiast parseInt, bo wartości mogą być dziesiętne
    const val = parseFloat(mmValue);
    if (isNaN(val)) return;

    const changeObj = { id: selectedObject.id };
    const pxValue = mmToPx(val);

    if (field === "x") changeObj.x = pxValue;
    else if (field === "y") changeObj.y = pxValue;
    // Sprawdzamy typ obiektu przed zmianą wymiarów
    else if (field === "width" && selectedObject._type !== 'text') changeObj.width = pxValue;
    else if (field === "height" && selectedObject._type !== 'text') changeObj.height = pxValue;
    else return; // Nie rób nic, jeśli pole to width/height dla tekstu

    // Wywołujemy odpowiedni callback w zależności od typu obiektu
    if (selectedObject._type === "image") {
      onChangeImage?.(changeObj);
    } else if (selectedObject._type === "svg") {
      onChangeSvg?.(changeObj);
    } else if (selectedObject._type === "text") {
      // Tekst nie powinien mieć zmienianych wymiarów w ten sposób
      if (field === 'x' || field === 'y') {
        onChangeText?.(changeObj);
      }
    }
  };

  // Handler dla klawisza Enter w polach input (z kopii zapasowej)
  const handleKeyDown = (field, e) => {
    if (e.key === "Enter") {
      handlePositionSizeConfirm(field, e.target.value);
      e.target.blur(); // Zdejmij focus z pola
    }
  };

  // Sekcja Rebusy (z kopii zapasowej)
  const [rebusesList, setRebusesList] = useState([]);

  useEffect(() => {
    // Pobieramy rebusy tylko jeśli panel jest aktywny
    if (panelMode === "rebuses") {
      const finalUrl = publicMode
        ? `${backendUrl}/public/rebuses`
        : `${backendUrl}/admin/rebuses`;
      fetch(finalUrl, { credentials: "include" })
        .then((res) => {
          if (!res.ok) {
            throw new Error(`Błąd pobierania rebusów, status=${res.status}`);
          }
          return res.json();
        })
        .then((data) => {
          setRebusesList(data);
        })
        .catch((err) => {
          console.error("[SidePanel][rebuses] =>", err);
          setRebusesList([]); // W razie błędu ustaw pustą listę
        });
    }
  }, [panelMode, publicMode, backendUrl]); // Zależności efektu

  // Handler "fałszywego" DnD dla rebusów (z kopii zapasowej, z poprawkami)
  const handleFakeRebusMouseDown = async (e, reb) => {
    e.preventDefault();
    e.stopPropagation();

    console.log("[SidePanel] handleFakeRebusMouseDown => rebus:", reb.name);
    // Upewnijmy się, że ścieżka do SVG istnieje
    if (!reb || !reb.svgPath) {
        console.error("[SidePanel] Brak ścieżki SVG dla rebusa:", reb);
        return;
    }
    const rawFileUrl = `${backendUrl}/${
      publicMode ? "public" : "admin"
    }/raw-file?path=${encodeURIComponent(reb.svgPath)}`;

    let dimensions = { width: 100, height: 100 }; // Domyślne wymiary
    try {
      // Używamy await, bo getSvgDimensions jest async
      const dim = await getSvgDimensions(rawFileUrl);
      dimensions = dim;
      console.log(`[SidePanel] => got dims for rebus ${reb.name}:`, dim);
    } catch (err) {
      console.warn(`[SidePanel] Błąd pobierania wymiarów SVG dla ${reb.name}, używam domyślnych 100x100:`, err);
    }

    // Sprawdzamy, czy callback istnieje i jest funkcją
    if (typeof onRebusFakeDndStart === "function") {
      onRebusFakeDndStart({
        svgUrl: rawFileUrl,
        width: dimensions.width,
        height: dimensions.height,
        // Dodajmy też nazwę i ID rebusa, może się przydać
        name: reb.name,
        rebusId: reb.id
      });
    } else {
        console.warn("[SidePanel] Callback onRebusFakeDndStart nie jest funkcją lub nie został przekazany.");
    }
  };

  // Warstwy - Handlery (z kopii zapasowej)
  const handleMoveUpClick = () => {
    console.log('[SIDE PANEL] handleMoveUpClick called');
    console.log('[SIDE PANEL] selectedShapeId:', selectedShapeId);
    console.log('[SIDE PANEL] onLayerUp function:', onLayerUp);
    if (!selectedShapeId) {
      console.log('[SIDE PANEL] No selectedShapeId, returning');
      return;
    }
    console.log('[SIDE PANEL] Calling onLayerUp with:', selectedShapeId);
    onLayerUp?.(selectedShapeId);
  };
  const handleMoveDownClick = () => {
    console.log('[SIDE PANEL] handleMoveDownClick called');
    console.log('[SIDE PANEL] selectedShapeId:', selectedShapeId);
    console.log('[SIDE PANEL] onLayerDown function:', onLayerDown);
    if (!selectedShapeId) {
      console.log('[SIDE PANEL] No selectedShapeId, returning');
      return;
    }
    console.log('[SIDE PANEL] Calling onLayerDown with:', selectedShapeId);
    onLayerDown?.(selectedShapeId);
  };
  const handleMoveTopClick = () => {
    console.log('[SIDE PANEL] handleMoveTopClick called');
    console.log('[SIDE PANEL] selectedShapeId:', selectedShapeId);
    console.log('[SIDE PANEL] onLayerTop function:', onLayerTop);
    if (!selectedShapeId) {
      console.log('[SIDE PANEL] No selectedShapeId, returning');
      return;
    }
    console.log('[SIDE PANEL] Calling onLayerTop with:', selectedShapeId);
    onLayerTop?.(selectedShapeId);
  };
  const handleMoveBottomClick = () => {
    console.log('[SIDE PANEL] handleMoveBottomClick called');
    console.log('[SIDE PANEL] selectedShapeId:', selectedShapeId);
    console.log('[SIDE PANEL] onLayerBottom function:', onLayerBottom);
    if (!selectedShapeId) {
      console.log('[SIDE PANEL] No selectedShapeId, returning');
      return;
    }
    console.log('[SIDE PANEL] Calling onLayerBottom with:', selectedShapeId);
    onLayerBottom?.(selectedShapeId);
  };

  // Rotacja - Handlery (z kopii zapasowej)
  const rotateLeft = () => {
    if (!selectedObject) return;
    handleRotate(selectedObject, -90, onChangeImage, onChangeSvg, onChangeText);
  };
  const rotateRight = () => {
    if (!selectedObject) return;
    handleRotate(selectedObject, 90, onChangeImage, onChangeSvg, onChangeText);
  };
  const flipX = () => {
    if (!selectedObject) return;
    handleFlipX(selectedObject, onChangeImage, onChangeSvg, onChangeText);
  };
  const flipY = () => {
    if (!selectedObject) return;
    handleFlipY(selectedObject, onChangeImage, onChangeSvg, onChangeText);
  };

  // Czcionki (z kopii zapasowej)
  const fontListContainerRef = useRef(null);
  const fontItemRefs = useRef({}); // Używamy obiektu do przechowywania refów
  const isText = selectedObject && selectedObject._type === "text";

  // Efekt do przewijania listy czcionek (z kopii zapasowej, z poprawkami)
  useEffect(() => {
    if (isText && selectedObject?.fontName && fontListContainerRef.current && Object.keys(fontItemRefs.current).length > 0) {
      const activeFontName = selectedObject.fontName;
      const activeElement = fontItemRefs.current[activeFontName];

      if (activeElement) {
        // Używamy requestAnimationFrame dla pewności, że element jest gotowy
        requestAnimationFrame(() => {
          activeElement.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
        });
      } else {
        console.warn(`[SidePanel] Nie znaleziono elementu dla czcionki: ${activeFontName}`);
      }
    }
  }, [isText, selectedObject?.fontName, availableFonts]); // Zależności efektu

  // Handler zmiany czcionki (z kopii zapasowej)
  const handleFontChange = (fontName) => {
    // Sprawdzamy, czy obiekt to tekst, ma ID i nie jest zablokowany w trybie publicznym
    if (isText && selectedObject?.id && !(publicMode && selectedObject?.flags?.locked)) {
      onChangeText({ id: selectedObject.id, fontName });
    }
  };

  // Prowadnice (z kopii zapasowej)
  const [orientation, setOrientation] = useState("H"); // Domyślnie pozioma
  const [guideExpression, setGuideExpression] = useState(""); // Wyrażenie dla nowej prowadnicy

  // Handler dodawania prowadnicy (z kopii zapasowej)
  function handleAddGuideClick() {
    // Sprawdzamy, czy callback istnieje i czy wyrażenie nie jest puste
    if (!onAddGuide || !guideExpression.trim()) return;
    onAddGuide({
      orientation,
      expression: guideExpression.trim()
    });
    setGuideExpression(""); // Czyścimy pole po dodaniu
  }

  // Funkcja do dodawania placeholder QR na projekt
  const handleAddPlaceholderQR = async () => {
    try {
      // Utworzenie fałszywego pliku placeholder QR
      // Użyjemy base64 obrazu placeholder QR lub URL do przykładowego QR
      const placeholderQRUrl = `${backendUrl}/api/placeholder-qr.png`; // Możemy utworzyć endpoint lub użyć base64
      
      // Symulujemy dodanie pliku poprzez onImageDropped
      const mockFile = {
        name: 'blissaro-placeholder-qr.png',
        size: 1024, // Rozmiar w bajtach (przykładowy)
        type: 'image/png'
      };

      // Tworzymy specjalny obiekt obrazu z flagą blissaroPlaceholder
      if (onImageDropped) {
        // Zamiast normalnego uploadu, bezpośrednio dodajemy obrazek z placeholder URL
        await onImageDropped({
          file: mockFile,
          name: mockFile.name,
          isBlissaroPlaceholder: true, // Specjalna flaga
          placeholderUrl: placeholderQRUrl
        });
      } else {
        throw new Error('Funkcja dodawania obrazów nie jest dostępna');
      }
    } catch (error) {
      console.error('[EditorSidePanel] Błąd dodawania placeholder QR:', error);
      throw error;
    }
  };

  // Funkcja do odświeżania projektu po aktualizacji QR
  const handleProjectUpdated = async () => {
    try {
      console.log('[EditorSidePanel] Odświeżanie projektu po aktualizacji QR...');
      
      // Powinienem otrzymać prop od rodzica do odświeżenia danych
      // Na razie po prostu pokazuję komunikat o powodzeniu
      showPopup({
        title: 'Powodzenie',
        message: '✅ Projekt został zaktualizowany z kodami QR! Odśwież stronę aby zobaczyć zmiany.',
        type: 'success',
        size: 'compact'
      });
      
    } catch (error) {
      console.error('[EditorSidePanel] Błąd odświeżania projektu:', error);
      throw error;
    }
  };

  // Stan dla popup
  const [popupConfig, setPopupConfig] = useState(null);
  const showPopup = (config) => setPopupConfig(config);
  const hidePopup = () => setPopupConfig(null);

  // Renderowanie komponentu (struktura z kopii zapasowej)
  return (
    <>
    <div
      style={{
        width: 300,
        // Usunięto height: "100%"
        backgroundColor: "#f8f9fa",
        borderLeft: "1px solid #dee2e6",
        padding: "16px",
        boxSizing: "border-box",
        display: 'flex', // Używamy flexboxa dla lepszego zarządzania przestrzenią
        flexDirection: 'column' // Elementy układane pionowo
      }}
    >
      {/* Panel wyboru trybu - tylko przycisk Prowadnice */}
      {!publicMode && (
        <div style={{ display: 'flex', borderBottom: '1px solid #ddd', marginBottom: '16px', flexShrink: 0 }}>
          {/* Zakładka Prowadnice */}
          <button
            onClick={() => setPanelMode('guides')}
            style={{
              flex: 1, padding: '10px',
              background: panelMode === 'guides' ? '#007bff' : '#f0f0f0',
              color: panelMode === 'guides' ? 'white' : 'black',
              border: 'none', cursor: 'pointer'
            }}
            title={tooltipsEnabled ? "Zarządzaj prowadnicami" : ""}
          >
            Prowadnice
          </button>
        </div>
      )}

      {/* Kontener dla środkowej zawartości - Usunięto overflowY: 'auto' */}
      <div style={{ flexGrow: 1, display: 'flex', flexDirection: 'column', marginBottom: '16px' /* Dodano margines dolny */ }}>
        {/* Sekcja Tekst */}
        {panelMode === "text" && (
          <div style={{ marginBottom: 16, display: 'flex', flexDirection: 'column' }}>
            {/* Przycisk Dodaj tekst - Dodano flexShrink: 0 */}
            <button
              onClick={onAddNewText} // Używamy bezpośrednio propa
              style={{
                display: "flex", width: "100%", alignItems: "center", justifyContent: "center",
                padding: "10px 12px", cursor: "pointer", backgroundColor: "#28a745",
                color: "#fff", border: "none", borderRadius: "4px", fontSize: "16px",
                boxShadow: "0 2px 4px rgba(0,0,0,0.1)", transition: "background-color 0.2s ease",
                marginBottom: "15px", flexShrink: 0 /* Zapobiegaj kurczeniu się przycisku */
              }}
              onMouseOver={(e) => (e.currentTarget.style.backgroundColor = "#218838")}
              onMouseOut={(e) => (e.currentTarget.style.backgroundColor = "#28a745")}
              title={tooltipsEnabled ? "Dodaj nowy blok tekstu na płótnie" : ""}
            >
              <span className="material-icons" style={{ marginRight: "8px" }}>add</span>
              Dodaj tekst
            </button>

            {/* Panel wyboru czcionki (tylko gdy zaznaczony jest tekst) - Usunięto flexGrow: 1 */}
            {isText && selectedObject && (
              <div style={{ marginTop: 12, border: "1px solid #dee2e6", borderRadius: "4px", padding: "12px", backgroundColor: "#fff", display: 'flex', flexDirection: 'column' }}>
                {/* Nagłówek - Dodano flexShrink: 0 */}
                <h4 style={{ fontSize: 16, fontWeight: 600, color: "#343a40", marginBottom: 10, borderBottom: "1px solid #eee", paddingBottom: 6, flexShrink: 0 }}>
                  Wybierz czcionkę
                </h4>
                {/* Kontener listy czcionek - Dodano overflowY: "auto" */}
                <div
                  ref={fontListContainerRef}
                  style={{ maxHeight: "800px", overflowY: "auto", paddingRight: "8px" }} // Dodano overflowY
                >
                  {availableFonts.length === 0 ? (
                    <p style={{ fontSize: 13, color: "#6c757d", textAlign: 'center' }}>Ładowanie lub brak czcionek...</p>
                  ) : (
                    availableFonts.map((f) => {
                      // Używamy fontName z obiektu tekstowego do porównania
                      const isCurrent = selectedObject.fontName === f.name;
                      // Sprawdzamy, czy obiekt jest zablokowany w trybie publicznym
                      const isDisabled = publicMode && selectedObject?.flags?.locked;
                      // Snippet tekstu lub domyślny
                      const snippet = selectedObject.text ? selectedObject.text.slice(0, 25) : "Przykład";

                      return (
                        <div
                          key={f.id || f.name} // Używamy ID jeśli dostępne, inaczej nazwy
                          ref={(el) => { fontItemRefs.current[f.name] = el; }} // Przypisujemy ref do obiektu
                          onClick={() => { if (!isDisabled) handleFontChange(f.name); }}
                          style={{
                            cursor: isDisabled ? "not-allowed" : "pointer",
                            padding: "8px 10px", marginBottom: 6,
                            border: isCurrent ? "2px solid #007bff" : "2px solid transparent",
                            backgroundColor: isCurrent ? "#e7f3ff" : isDisabled ? "#e9ecef" : "#fff",
                            opacity: isDisabled ? 0.65 : 1,
                            borderRadius: '3px', // Dodano lekko zaokrąglone rogi
                            transition: 'background-color 0.15s ease-in-out, border-color 0.15s ease-in-out' // Płynne przejścia
                          }}
                          title={tooltipsEnabled ? `${f.name} ${isDisabled ? '(Zablokowane)' : ''}`: f.name}
                          onMouseOver={(ev) => { if (!isDisabled && !isCurrent) ev.currentTarget.style.backgroundColor = "#f8f9fa"; }}
                          onMouseOut={(ev) => { if (!isDisabled && !isCurrent) ev.currentTarget.style.backgroundColor = "#fff"; }}
                        >
                          {/* ID czcionki (jeśli istnieje) */}
                          {f.id && (
                            <span style={{ fontSize: '11px', color: '#6c757d', fontFamily: 'sans-serif', marginRight: '8px' }}>
                              ({f.id})
                            </span>
                          )}
                          {/* Podgląd czcionki */}
                          <span style={{ fontFamily: `"${f.name}"`, fontSize: "17px", color: isDisabled ? "#6c757d" : "#212529" }}>
                            {snippet || "AaBbCc"}
                          </span>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Sekcja Upload (z kopii zapasowej) */}
        {panelMode === "upload" && (
          <div style={{ marginBottom: 20, padding: 15, borderRadius: "8px", backgroundColor: "#fff", border: "1px solid #dee2e6" }}>
            <h4 style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "18px", fontWeight: 600, color: "#343a40", marginBottom: 12 }}>
              <span className="material-icons" style={{ fontSize: 24 }}>cloud_upload</span>
              Upload obrazu/SVG
            </h4>
            {/* Używamy DropZone z odpowiednim propem */}
            <DropZone onImageDropped={onImageDropped} />
            <p style={{ fontSize: 12, color: "#6c757d", marginTop: 10, textAlign: 'center' }}>
              Przeciągnij plik tutaj lub kliknij, aby wybrać.<br/>
              Obsługiwane formaty: JPG, PNG, SVG.
            </p>
          </div>
        )}

        {/* Sekcja Rebusy */}
        {panelMode === "rebuses" && (
          <div style={{ marginBottom: 20, padding: 15, borderRadius: "8px", backgroundColor: "#fff", border: "1px solid #dee2e6" }}>
            <h4 style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "18px", fontWeight: 600, color: "#343a40", marginBottom: 12 }}>
              <span className="material-icons" style={{ fontSize: 24 }}>image_search</span>
              Wybierz Rebus
            </h4>
            {rebusesList.length === 0 ? (
              <p style={{ fontSize: 14, color: "#6c757d", textAlign: "center", padding: "10px 0" }}>
                Ładowanie lub brak rebusów...
              </p>
            ) : (
              // Grid z rebusami
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(90px, 1fr))", gap: "12px", marginTop: "12px" }}>
                {rebusesList.map((reb) => {
                  // Upewniamy się, że ścieżka istnieje
                  const path = reb.previewPath || reb.svgPath;
                  if (!path) return null; // Pomijamy rebusy bez ścieżki
                  const previewUrl = `${backendUrl}/${publicMode ? "public" : "admin"}/raw-file?path=${encodeURIComponent(path)}`;

                  return (
                    <div
                      key={reb.id}
                      // Handler "fałszywego" DnD
                      // Dodano return false; aby dodatkowo spróbować zapobiec domyślnej akcji
                      onMouseDown={(e) => { handleFakeRebusMouseDown(e, reb); return false; }}
                      style={{
                        cursor: "grab", borderRadius: "4px", overflow: "hidden",
                        border: "1px solid #eee", transition: "transform 0.2s ease, box-shadow 0.2s ease",
                        textAlign: "center", backgroundColor: "#f8f9fa", opacity: 1
                      }}
                      onMouseOver={(ev) => {
                        ev.currentTarget.style.transform = "scale(1.03)";
                        ev.currentTarget.style.boxShadow = "0 4px 8px rgba(0,0,0,0.1)";
                      }}
                      onMouseOut={(ev) => {
                        ev.currentTarget.style.transform = "scale(1)";
                        ev.currentTarget.style.boxShadow = "none";
                      }}
                      title={tooltipsEnabled ? `Kliknij lub przeciągnij: ${reb.name}` : reb.name}
                    >
                      <img
                        src={previewUrl}
                        alt={reb.name || "Rebus"}
                        style={{
                          width: "80%", height: "auto", margin: "5% auto", display: "block",
                          objectFit: "contain", maxHeight: "70px",
                          // Zapobiegamy interakcji z obrazkiem
                          pointerEvents: "none", userSelect: "none", WebkitUserSelect: "none", MozUserSelect: "none"
                        }}
                        // Zapobiegamy domyślnemu przeciąganiu obrazka
                        draggable="false"
                      />
                      <div style={{
                        fontSize: 11, color: "#495057", padding: "4px 2px",
                        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                        // Zapobiegamy zaznaczaniu tekstu
                        pointerEvents: "none", userSelect: "none", WebkitUserSelect: "none", MozUserSelect: "none"
                      }}>
                        {reb.name || "Rebus"}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            <p style={{ fontSize: 12, color: "#6c757d", marginTop: "15px", textAlign: "center" }}>
              (Kliknij lub przeciągnij rebus, aby umieścić na płótnie)
            </p>
          </div>
        )}

        {/* Sekcja Prowadnice (z kopii zapasowej, tylko jeśli nie publicMode) */}
        {panelMode === "guides" && !publicMode && (
          <div style={{ marginTop: 16, padding: 15, borderRadius: "8px", backgroundColor: "#fff", border: "1px solid #dee2e6" }}>
            <h4 style={{ fontSize: "18px", fontWeight: 600, color: "#343a40", marginBottom: 12 }}>
              Prowadnice Standardowe
            </h4>
            {/* Formularz dodawania prowadnicy */}
            <div style={{ display: "flex", flexDirection: "column", gap: "10px", marginBottom: '15px' }}>
              <select
                value={orientation}
                onChange={(e) => setOrientation(e.target.value)}
                style={{ fontSize: 14, padding: "8px 10px", border: "1px solid #ced4da", borderRadius: "4px" }}
                title={tooltipsEnabled ? "Wybierz orientację prowadnicy" : ""}
              >
                <option value="H">Pozioma (H)</option>
                <option value="V">Pionowa (V)</option>
              </select>
              <input
                type="text"
                value={guideExpression}
                onChange={(e) => setGuideExpression(e.target.value)}
                placeholder='np. "50%", "10mm", "100% - 15mm"'
                style={{ padding: "8px 10px", border: "1px solid #ced4da", borderRadius: "4px", fontSize: 14 }}
                title={tooltipsEnabled ? 'Wpisz pozycję (np. "50%", "10mm", "100% - 15mm")' : ""}
              />
              <button
                onClick={handleAddGuideClick}
                style={{
                  border: "none", padding: "10px", backgroundColor: "#007bff", color: "#fff",
                  borderRadius: "4px", cursor: "pointer", fontSize: 15, transition: "background-color 0.2s ease"
                }}
                onMouseOver={(ev) => (ev.currentTarget.style.background = "#0056b3")}
                onMouseOut={(ev) => (ev.currentTarget.style.background = "#007bff")}
                title={tooltipsEnabled ? "Dodaj nową prowadnicę" : ""}
              >
                Dodaj Prowadnicę
              </button>
            </div>

            {/* Przyciski predefiniowanych prowadnic pionowych */}
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', marginTop: '10px', marginBottom: '15px' }}>
              {['25%', '50%', '75%'].map(percent => (
                <button
                  key={percent}
                  onClick={() => onAddGuide({ orientation: 'V', expression: percent })}
                  style={{
                    flex: 1, border: "1px solid #007bff", padding: "8px 5px", backgroundColor: "#fff", color: "#007bff",
                    borderRadius: "4px", cursor: "pointer", fontSize: 14, transition: "background-color 0.2s ease, color 0.2s ease"
                  }}
                  onMouseOver={(ev) => { ev.currentTarget.style.background = "#007bff"; ev.currentTarget.style.color = "#fff"; }}
                  onMouseOut={(ev) => { ev.currentTarget.style.background = "#fff"; ev.currentTarget.style.color = "#007bff"; }}
                  title={tooltipsEnabled ? `Dodaj prowadnicę pionową na ${percent}` : ""}
                >
                  V {percent}
                </button>
              ))}
            </div>

            {/* Lista istniejących prowadnic */}
            {currentPageData?.guides?.length > 0 && (
              <div style={{ marginTop: 20 }}>
                <h5 style={{ fontSize: "16px", fontWeight: 600, color: "#343a40", borderTop: "1px solid #eee", paddingTop: 10 }}>
                  Lista prowadnic standardowych
                </h5>
                <ul style={{ marginTop: 10, paddingLeft: 0, listStyle: "none", maxHeight: '150px', overflowY: 'auto' }}>
                  {currentPageData.guides.map((g, index) => (
                    <li
                      key={g.id ?? index} 
                      style={{
                        marginBottom: 8, display: "flex", justifyContent: "space-between",
                        alignItems: "center", fontSize: 14, borderBottom: "1px dashed #eee", paddingBottom: 4
                      }}
                    >
                      <span>{g.orientation}: {g.expression}</span>
                      <button
                        onClick={() => onRemoveGuide(g.id ?? index)}
                        title={tooltipsEnabled ? "Usuń tę prowadnicę" : "Usuń prowadnicę"}
                        style={{
                          marginLeft: 10, padding: "2px 6px", border: "none", background: "#dc3545",
                          color: "#fff", borderRadius: "4px", cursor: "pointer", fontSize: 12
                        }}
                      >
                        Usuń
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Sekcja Linia Zgięcia */}
            <div style={{ marginTop: 20, borderTop: '1px solid #ddd', paddingTop: '15px' }}>
              <h4 style={{ fontSize: "18px", fontWeight: 600, color: "#343a40", marginBottom: 12 }}>
                Linia Zgięcia
              </h4>
              <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                <button
                  onClick={() => onUpdateFoldLineConfig({ type: 'vertical', text: 'linia zgięcia', color: '#FF0000' })}
                  disabled={foldLineConfig?.type === 'vertical'}
                  style={{ border: "1px solid #007bff", padding: "8px", backgroundColor: foldLineConfig?.type === 'vertical' ? "#007bff" : "#fff", color: foldLineConfig?.type === 'vertical' ? "#fff" : "#007bff", borderRadius: "4px", cursor: "pointer" }}
                  title={tooltipsEnabled ? "Dodaj pionową linię zgięcia (na 50% szerokości)" : ""}
                >
                  Dodaj Pionową
                </button>
                <button
                  onClick={() => onUpdateFoldLineConfig({ type: 'horizontal', text: 'linia zgięcia', color: '#FF0000' })}
                  disabled={foldLineConfig?.type === 'horizontal'}
                  style={{ border: "1px solid #007bff", padding: "8px", backgroundColor: foldLineConfig?.type === 'horizontal' ? "#007bff" : "#fff", color: foldLineConfig?.type === 'horizontal' ? "#fff" : "#007bff", borderRadius: "4px", cursor: "pointer" }}
                  title={tooltipsEnabled ? "Dodaj poziomą linię zgięcia (na 50% wysokości)" : ""}
                >
                  Dodaj Poziomą
                </button>
                <button
                  onClick={() => onUpdateFoldLineConfig(null)}
                  disabled={!foldLineConfig || !foldLineConfig.type}
                  style={{ border: "1px solid #dc3545", padding: "8px", backgroundColor: "#fff", color: "#dc3545", borderRadius: "4px", cursor: "pointer" }}
                  title={tooltipsEnabled ? "Usuń linię zgięcia" : ""}
                >
                  Usuń Linię Zgięcia
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Panel Blissaro QR (dostępny w trybie publicznym i admina) */}
        {panelMode === "blissaro" && (
          <BlissaroQRPanel 
            backendUrl={backendUrl}
            projectId={projectId}
            orderEmail={orderEmail}
            tooltipsEnabled={tooltipsEnabled}
            onAddPlaceholderQR={handleAddPlaceholderQR}
            onProjectUpdated={handleProjectUpdated}
            onManualSave={onManualSave}
            publicKey={publicKey}
            onEmailUpdated={onEmailUpdated}
            publicMode={publicMode}
          />
        )}
      </div> {/* Koniec kontenera przewijalnego */}

      {/* Sekcje wspólne dla zaznaczonego obiektu (na dole, poza kontenerem przewijalnym) */}
      {selectedObject && (
        <div style={{ borderTop: '1px solid #ddd', paddingTop: '16px', flexShrink: 0 }}>
          {/* Warstwy (z kopii zapasowej) */}
          <div style={{ marginBottom: 16, padding: 15, borderRadius: "8px", backgroundColor: "#fff", border: "1px solid #dee2e6" }}>
            <h4 style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "18px", fontWeight: 600, color: "#343a40", marginBottom: 12 }}>
              <span className="material-icons" style={{ fontSize: 24 }}>layers</span>
              Warstwy
            </h4>
            <div style={{ display: "flex", justifyContent: "space-around", gap: "8px", marginTop: "8px" }}>
              {[
                { handler: handleMoveTopClick, icon: "vertical_align_top", title: "Na wierzch" },
                { handler: handleMoveBottomClick, icon: "vertical_align_bottom", title: "Na spód" },
                { handler: handleMoveUpClick, icon: "arrow_upward", title: "Do góry" },
                { handler: handleMoveDownClick, icon: "arrow_downward", title: "Do dołu" }
              ].map((btn) => (
                <button
                  key={btn.title}
                  onClick={btn.handler}
                  title={tooltipsEnabled ? btn.title : ""}
                  style={{ border: "none", background: "transparent", cursor: "pointer", padding: "6px", borderRadius: "4px", transition: "background-color 0.2s ease" }}
                  onMouseOver={(ev) => (ev.currentTarget.style.background = "#e9ecef")}
                  onMouseOut={(ev) => (ev.currentTarget.style.background = "transparent")}
                >
                  <span className="material-icons" style={{ fontSize: 24, color: "#495057", verticalAlign: "middle" }}>{btn.icon}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Pozycja i wymiary (z kopii zapasowej) */}
          <div style={{ marginBottom: 16, padding: 15, borderRadius: "8px", backgroundColor: "#fff", border: "1px solid #dee2e6" }}>
            <h4 style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "18px", fontWeight: 600, color: "#343a40", marginBottom: 12 }}>
              <span className="material-icons" style={{ fontSize: 24 }}>straighten</span>
              Pozycja i wymiary (mm)
            </h4>
            <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "10px 8px", alignItems: "center" }}>
              {/* X */}
              <label htmlFor="pos-x" style={{ justifySelf: "end", fontSize: 14, color: "#495057" }}>X:</label>
              <input id="pos-x" type="number" step="0.1" value={xMm}
                     onChange={(e) => setXmm(e.target.value)}
                     onBlur={(e) => handlePositionSizeConfirm("x", e.target.value)}
                     onKeyDown={(e) => handleKeyDown("x", e)}
                     style={{ width: "100%", padding: "6px 8px", border: "1px solid #ced4da", borderRadius: "4px", fontSize: 14 }}
                     placeholder="X" disabled={publicMode && selectedObject?.flags?.locked}
                     title={tooltipsEnabled ? "Pozycja X (w mm)" : ""} />
              {/* Y */}
              <label htmlFor="pos-y" style={{ justifySelf: "end", fontSize: 14, color: "#495057" }}>Y:</label>
              <input id="pos-y" type="number" step="0.1" value={yMm}
                     onChange={(e) => setYmm(e.target.value)}
                     onBlur={(e) => handlePositionSizeConfirm("y", e.target.value)}
                     onKeyDown={(e) => handleKeyDown("y", e)}
                     style={{ width: "100%", padding: "6px 8px", border: "1px solid #ced4da", borderRadius: "4px", fontSize: 14 }}
                     placeholder="Y" disabled={publicMode && selectedObject?.flags?.locked}
                     title={tooltipsEnabled ? "Pozycja Y (w mm)" : ""} />
              {/* Szerokość */}
              <label htmlFor="size-w" style={{ justifySelf: "end", fontSize: 14, color: "#495057" }}>Szer:</label>
              <input id="size-w" type="number" step="0.1" value={wMm}
                     onChange={(e) => setWmm(e.target.value)}
                     onBlur={(e) => handlePositionSizeConfirm("width", e.target.value)}
                     onKeyDown={(e) => handleKeyDown("width", e)}
                     style={{ width: "100%", padding: "6px 8px", border: "1px solid #ced4da", borderRadius: "4px", fontSize: 14 }}
                     placeholder="Szerokość"
                     disabled={(publicMode && selectedObject?.flags?.locked) || selectedObject?._type === "text"}
                     title={tooltipsEnabled ? (selectedObject?._type === "text" ? "Szerokość tekstu jest automatyczna" : "Szerokość (w mm)") : ""} />
              {/* Wysokość */}
              <label htmlFor="size-h" style={{ justifySelf: "end", fontSize: 14, color: "#495057" }}>Wys:</label>
              <input id="size-h" type="number" step="0.1" value={hMm}
                     onChange={(e) => setHmm(e.target.value)}
                     onBlur={(e) => handlePositionSizeConfirm("height", e.target.value)}
                     onKeyDown={(e) => handleKeyDown("height", e)}
                     style={{ width: "100%", padding: "6px 8px", border: "1px solid #ced4da", borderRadius: "4px", fontSize: 14 }}
                     placeholder="Wysokość"
                     disabled={(publicMode && selectedObject?.flags?.locked) || selectedObject?._type === "text"}
                     title={tooltipsEnabled ? (selectedObject?._type === "text" ? "Wysokość tekstu jest automatyczna" : "Wysokość (w mm)") : ""} />
            </div>
          </div>

          {/* Obrót / Odbicie (z kopii zapasowej) */}
          <div style={{ marginBottom: 16, padding: 15, borderRadius: "8px", backgroundColor: "#fff", border: "1px solid #dee2e6" }}>
            <h4 style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "18px", fontWeight: 600, color: "#343a40", marginBottom: 12 }}>
              <span className="material-icons" style={{ fontSize: 24 }}>transform</span>
              Obrót / Odbicie
            </h4>
            <div style={{ display: "flex", justifyContent: "space-around", gap: "8px", marginTop: "8px" }}>
              {[
                { handler: rotateLeft, icon: "rotate_left", title: "Obrót -90°" },
                { handler: rotateRight, icon: "rotate_right", title: "Obrót +90°" },
                { handler: flipY, icon: "flip", title: "Odbicie poziome" }, // flipY to odbicie poziome (lustrzane w pionie)
                { handler: flipX, icon: "flip", title: "Odbicie pionowe" }  // flipX to odbicie pionowe (lustrzane w poziomie)
              ].map((btn) => {
                const isDisabled = publicMode && selectedObject?.flags?.locked;
                return (
                  <button
                    key={btn.title}
                    onClick={btn.handler}
                    title={tooltipsEnabled ? btn.title : ""}
                    style={{
                      border: "none", background: "transparent", cursor: isDisabled ? "not-allowed" : "pointer",
                      padding: "6px", borderRadius: "4px", transition: "background-color 0.2s ease",
                      opacity: isDisabled ? 0.5 : 1
                    }}
                    disabled={isDisabled}
                    onMouseOver={(ev) => { if (!isDisabled) ev.currentTarget.style.background = "#e9ecef"; }}
                    onMouseOut={(ev) => (ev.currentTarget.style.background = "transparent")}
                  >
                    <span className="material-icons" style={{
                      fontSize: 24, color: "#495057", verticalAlign: "middle",
                      // Dodajemy transformację ikony dla odbicia pionowego (flipX)
                      transform: btn.handler === flipX ? "rotate(90deg)" : "none"
                    }}>
                      {btn.icon}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>

    {/* Popup */}
    {popupConfig && (
      <Popup 
        config={popupConfig}
        onClose={hidePopup}
      />
    )}
  </>
  );
}
